import { useRouter } from 'next/router';
import { useEffect, useMemo } from 'react';

import { PageSkeleton } from '@kc-console/common-ui';
import { DetailLayout, DetailSection } from '@kc-console/layout';
import { PageHeader } from '@kc-console/shared-ui';

import useGetConsoleEvent from '../model/useGetConsoleEvent';
import EventDetailContent from './EventDetailContent';

import { getRouterConfig, ROUTE_PATH } from '@/shared/config/routes';
import { useRegionEndpoint } from '@/shared/hooks';
import useBreadcrumbItemsWithRegion from '@/shared/hooks/useBreadcrumbItemsWithRegion';

/**
 * Status Board 이벤트 상세 페이지 템플릿.
 * @description
 * - Breadcrumb에 이벤트 제목을 동적으로 노출한다.
 * - 상세 조회 로딩 중에는 Detail 스켈레톤을 표시하고, 오류 시 목록으로 되돌린다.
 * @example
 * <EventDetailTemplate />
 */
const EventDetailTemplate = () => {
  const router = useRouter();
  const { currentRegion } = useRegionEndpoint();
  const eventId = typeof router.query.eventId === 'string' ? router.query.eventId : '';

  const { data: event, isLoading, isError } = useGetConsoleEvent({ eventId });

  const routerConfig = useMemo(() => getRouterConfig({ eventTitle: event?.title }), [event?.title]);

  const breadcrumbs = useBreadcrumbItemsWithRegion(routerConfig);

  useEffect(() => {
    if (isError) {
      router.replace(`${ROUTE_PATH.EVENT_LIST}?region=${currentRegion}`);
    }
  }, [isError, router, currentRegion]);

  if (isError || isLoading || !event) {
    return <PageSkeleton variant="detail" />;
  }

  return (
    <DetailLayout>
      <DetailSection headerView={<PageHeader title={event.title} breadcrumbs={breadcrumbs} />}>
        <EventDetailContent event={event} />
      </DetailSection>
    </DetailLayout>
  );
};

export default EventDetailTemplate;
